import React, { useState, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import TagsList from "../More";
// import { useAuth } from "../../../context/AuthContext";
import SimilarProfiles from "../SimilarProfiles/SimilarProfiles";
// import { similarProfiles } from "./SimilarProfilesData";
import ProfileAbout from "../../ProfileAbout/ProfileAbout";
import FindPerfectMentor from "../FindPerfectMentor/FindPerfectMentor";
import WorkExperience from "./WorkExperience";
import EducationSummary from "./EducationSummary";
import PublicationSummary from "./PublicationSummary";
import Projects from "./Projects";
import Achievements from "./Achievements";
import ProfileSummary from "./ProfileSummary";
import Loader from "../../../components/Loader/Loader";
import { getCountryMap } from "../../../utils/countryCache";
import {
  useGetMentorshipOfferedQuery,
  useGetSubjectExpertiseQuery,
} from "../../../api/apiSlice";
import {
  buildIdNameMap,
  extractSelectedIds,
} from "../../AccordionCategory/accordionTree.helpers";
import styles from "./FullProfile.module.css";

function openWhatsAppChat() {
  const phoneNumber = "917067659049";
  const message = "Hi";
  const encodedMessage = encodeURIComponent(message);

  const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

  const url = isMobile
    ? `https://wa.me/${phoneNumber}?text=${encodedMessage}`
    : `https://web.whatsapp.com/send?phone=${phoneNumber}&text=${encodedMessage}`;

  window.open(url, "_blank");
}

export default function FullProfile(props) {
  const { pathname } = useLocation();
  const role = pathname.includes("mentors") ? "mentor" : "author";
  const { profile, isOpen, similarProfiles } = props;
  const { subjectMatterExpertise, mentorshipOffered } = profile;
  const [activeTab, setActiveTab] = useState("summary");
  const [isMobile, setIsMobile] = useState(false);
  const [countryName, setCountryName] = useState(profile.country);

  useEffect(() => {
    let mounted = true;

    getCountryMap().then((map) => {
      if (mounted && map[profile.country]) {
        setCountryName(map[profile.country]);
      }
    });

    return () => {
      mounted = false;
    };
  }, [profile.country]);

  const tabs = [
    { key: "summary", label: "Profile Summary" },
    { key: "experience", label: "Work Experience" },
    { key: "education", label: "Education" },
    { key: "projects", label: "Projects" },
    { key: "achievements", label: "Achievements" },
    { key: "publications", label: "Publications" },
  ];

  const swiperRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        if (swiperRef.current?.swiper) {
          swiperRef.current.swiper.update();
        }
      }, 100);
    }
  }, [isOpen]);

  // Detect mobile screen
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize(); // check on mount
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const renderContent = (tabKey) => {
    switch (tabKey) {
      case "summary":
        return (
          <section>
            <ProfileSummary data={profile} />
          </section>
        );
      case "experience":
        return (
          <section>
            <WorkExperience data={profile} />
          </section>
        );
      case "education":
        return (
          <section>
            <EducationSummary data={profile} />
          </section>
        );
      case "projects":
        return (
          <section>
            <Projects data={profile} />
          </section>
        );
      case "achievements":
        return (
          <section>
            <Achievements data={profile} />
          </section>
        );
      case "publications":
        return (
          <section>
            <PublicationSummary data={profile} />
          </section>
        );
      default:
        return null;
    }
  };

  const { data: subjectExpertiseRes, isLoading: subjectExpertiseLoading } =
    useGetSubjectExpertiseQuery();
  const { data: mentorshipOfferedRes, isLoading: mentorshipOfferedLoading } =
    useGetMentorshipOfferedQuery();

  const subjectExpertiseList = subjectExpertiseRes?.data ?? [];
  const mentorshipOfferedList = mentorshipOfferedRes?.data ?? [];
  const subjectTags = React.useMemo(() => {
    if (!subjectMatterExpertise || !subjectExpertiseList.length) return [];

    const idNameMap = buildIdNameMap(subjectExpertiseList);
    const selectedIds = extractSelectedIds(subjectMatterExpertise);

    return selectedIds.map((id) => idNameMap[id]).filter(Boolean);
  }, [subjectMatterExpertise, subjectExpertiseList]);

  const mentorshipTags = React.useMemo(() => {
    if (!mentorshipOffered || !mentorshipOfferedList.length) return [];

    const idNameMap = buildIdNameMap(mentorshipOfferedList);
    const selectedIds = extractSelectedIds(mentorshipOffered);

    return selectedIds.map((id) => idNameMap[id]).filter(Boolean);
  }, [mentorshipOffered, mentorshipOfferedList]);

  return (
    <>
      <div className={styles.container}>
        <div className={styles.mainContainer}>
          <div className={styles.leftSection}>
            <div className={styles.card}>
              {/* Left Side - Image */}
              <div className={styles.left}>
                <div className={styles.imageWrapper}>
                  <img
                    src={profile.photograph}
                    alt={profile.fullName}
                    className={styles.profileImage}
                  />
                </div>
              </div>

              {/* Right Side - Content */}
              <div className={styles.right}>
                <div className={styles.headingOuter}>
                  <div className={styles.leftHeading}>
                    <div className={styles.header}>
                      <h3>{profile.fullName}</h3>
                      {profile.imparcId && (
                        <span className={styles.badge}>{profile.imparcId}</span>
                      )}
                    </div>
                  </div>
                </div>

                <div className={styles.ratingLocation}>
                  <div className={styles.location}>{countryName}</div>
                  {/* <div className={styles.ratingContainer}>
                    <div className={styles.rating}>
                      {Array.from({ length: rating }, (_, i) => (
                        <span key={i}>★</span>
                      ))}
                    </div>
                    <span className={styles.currency}> {profile.currency}</span>{" "}
                    <span className={styles.rate}> {profile.rate}/hr</span>
                  </div> */}
                </div>
                <p className={styles.title}>
                  {profile.currentDesignation} | {profile.departmentFacultyName}{" "}
                  | {profile.institutionName}
                </p>

                {/* {profile.tags && <TagsList tags={profile.tags} />} */}
                {role === "mentor" && (
                  <>
                    <p className={styles.subjectHeading}>
                      <span className={styles.blue}>Subject</span>{" "}
                      <span className={styles.orange}>Matter Expertise</span>
                    </p>

                    {subjectExpertiseLoading ? (
                      <Loader />
                    ) : subjectTags.length > 0 ? (
                      <TagsList tags={subjectTags} />
                    ) : (
                      <div className={styles.noData}>No Data Available.</div>
                    )}

                    <p className={styles.subjectHeading}>
                      <span className={styles.blue}>Mentorship </span>{" "}
                      <span className={styles.orange}>Offered</span>
                    </p>
                    {mentorshipOfferedLoading ? (
                      <Loader />
                    ) : mentorshipTags.length ? (
                      <TagsList tags={mentorshipTags} />
                    ) : (
                      <div className={styles.noData}>No Data Available.</div>
                    )}
                  </>
                )}
                <div className={styles.buttons}>
                  {/* <button
                    className={styles.createProject}
                    onClick={() => setAuthModal({ open: true, tab: "login" })}
                  >
                    Create Project
                  </button> */}
                  <button
                    className={styles.contactBtn}
                    onClick={openWhatsAppChat}
                  >
                    Contact {profile.fullName}
                  </button>
                </div>
              </div>
            </div>

            {/* Tabs / Accordion */}
            <div className={styles.tabOuter}>
              {!isMobile ? (
                <>
                  <div className={styles.tabNav}>
                    {tabs.map((tab) => (
                      <button
                        key={tab.key}
                        className={`${styles.tabBtn} ${activeTab === tab.key ? styles.active : ""
                          }`}
                        onClick={() => setActiveTab(tab.key)}
                      >
                        <span>{tab.label}</span>
                      </button>
                    ))}
                  </div>
                  <div className={styles.tabContent}>
                    {renderContent(activeTab)}
                  </div>
                </>
              ) : (
                <div className={styles.accordion}>
                  {tabs.map((tab) => (
                    <div key={tab.key} className={styles.accordionItem}>
                      <button
                        className={`${styles.accordionBtn} ${activeTab === tab.key ? styles.active : ""
                          }`}
                        onClick={() =>
                          setActiveTab(activeTab === tab.key ? "" : tab.key)
                        }
                      >
                        {tab.label}
                      </button>
                      {activeTab === tab.key && (
                        <div className={styles.accordionContent}>
                          {renderContent(tab.key)}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className={styles.similarProfiles}>
              <div className={styles.similarProfilesHeading}>
                Similar Profiles
              </div>
              {similarProfiles?.length > 0 ? <SimilarProfiles profiles={similarProfiles} role={role} /> : <div className={styles.noData}>No Data Available.</div>}
            </div>
          </div>

          <div className={styles.rightSection}>
            <ProfileAbout
              profile={profile}
              countryName={countryName}
              memberSince={profile.memberSince}
              role={role}
            />
          </div>
        </div>
      </div>
      <FindPerfectMentor />
    </>
  );
}
